import React from "react";
import { useTranslation } from "react-i18next";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import ClearIcon from "@mui/icons-material/Clear";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { styled } from "@mui/material/styles";
import { getBlockLabel, getBlockTypeConfig } from "../config/blockTypes";
import type { PlatformBlock } from "../types";

const PREFIX = "BlockItem";
const classes = {
  addZoneTop: `${PREFIX}-addZoneTop`,
  addZoneBottom: `${PREFIX}-addZoneBottom`,
  addButton: `${PREFIX}-addButton`,
  card: `${PREFIX}-card`,
  cardContent: `${PREFIX}-cardContent`,
  dragHandle: `${PREFIX}-dragHandle`,
  blockIcon: `${PREFIX}-blockIcon`,
  blockTitle: `${PREFIX}-blockTitle`,
};

const Root = styled(Box)(({ theme }) => ({
  position: "relative",
  overflow: "visible",

  [`& .${classes.addZoneTop}`]: {
    position: "relative",
    height: 40,
    marginTop: -24,
    marginBottom: -16,
    zIndex: 1,
    [`& .${classes.addButton}`]: {
      opacity: 0,
      transition: "opacity 0.2s ease-in-out",
      position: "absolute",
      left: "50%",
      top: "50%",
      transform: "translate(-50%, -50%)",
    },
    "&:hover": {
      [`& .${classes.addButton}`]: { opacity: 1 },
    },
  },

  [`& .${classes.addZoneBottom}`]: {
    position: "relative",
    height: 40,
    marginTop: -16,
    marginBottom: -24,
    zIndex: 1,
    [`& .${classes.addButton}`]: {
      opacity: 0,
      transition: "opacity 0.2s ease-in-out",
      position: "absolute",
      left: "50%",
      top: "50%",
      transform: "translate(-50%, -50%)",
    },
    "&:hover": {
      [`& .${classes.addButton}`]: { opacity: 1 },
    },
  },

  [`& .${classes.card}`]: {
    overflow: "visible",
    position: "relative",
    display: "block",
  },

  [`& .${classes.cardContent}`]: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(2),
  },

  [`& .${classes.dragHandle}`]: {
    display: "flex",
    alignItems: "center",
    cursor: "grab",
    color: theme.palette.action.active,
  },

  [`& .${classes.blockIcon}`]: {
    display: "flex",
    alignItems: "center",
    "& svg": { fontSize: 32 },
  },

  [`& .${classes.blockTitle}`]: {
    flex: 1,
    minWidth: 0,
  },
}));

interface BlockItemProps {
  block: PlatformBlock;
  fieldName: string;
  index: number;
  isDragging: boolean;
  draggableStyle?: React.CSSProperties;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  onCreateAbove?: (index: number) => void;
  onCreateBelow?: (index: number) => void;
  provided: any;
  snapshot: any;
}

/**
 * Компонент для отображения одного элемента блока в списке
 */
export const BlockItem: React.FC<BlockItemProps> = ({
  block,
  index,
  draggableStyle,
  onEdit,
  onDelete,
  onCreateAbove,
  onCreateBelow,
  provided,
  snapshot,
}) => {
  const { t } = useTranslation();
  const blockConfig = getBlockTypeConfig(block.component);

  const itemStyle: React.CSSProperties = {
    userSelect: "none",
    opacity: (block as any).deleted ? 0.2 : 1,
    ...draggableStyle,
  };

  return (
    <Root>
      {onCreateAbove && (
        <Box className={classes.addZoneTop}>
          <IconButton
            className={classes.addButton}
            onClick={() => onCreateAbove(index)}
            size="small"
          >
            <AddCircleIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
      <Paper
        ref={provided.innerRef}
        {...provided.draggableProps}
        style={itemStyle}
        className={classes.card}
        sx={{
          border: snapshot.isDragging ? "2px solid" : "none",
          borderColor: "primary.main",
        }}
      >
        <Box className={classes.cardContent}>
          <Box
            {...provided.dragHandleProps}
            className={classes.dragHandle}
            title={t("platformBlocks.item.reorder")}
          >
            <DragIndicatorIcon fontSize="small" />
          </Box>
          <Box className={classes.blockIcon}>
            {blockConfig ? (
              blockConfig.icon
            ) : (
              <Box sx={{ width: 32, height: 32 }} />
            )}
          </Box>
          <Box className={classes.blockTitle}>
            <Typography variant="subtitle2">
              {getBlockLabel(block, t)}
            </Typography>
            {blockConfig?.notRendered && (
              <Typography variant="caption" color="warning.main">
                {t("platformBlocks.item.notRendered")}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={0.5}>
            <IconButton
              onClick={() => onEdit(index)}
              title={t("platformBlocks.item.edit")}
              size="small"
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={() => onDelete(index)}
              title={t("platformBlocks.item.delete")}
              size="small"
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
      </Paper>
      {onCreateBelow && (
        <Box className={classes.addZoneBottom}>
          <IconButton
            className={classes.addButton}
            onClick={() => onCreateBelow(index)}
            size="small"
          >
            <AddCircleIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Root>
  );
};
